import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { FileArrowDown, UploadSimple, X } from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { Meeting, MeetingFile } from '@/types/meeting';
import { paths } from '@/paths';
import { dayjs } from '@/lib/v2/dayjs';
import axios from '@/lib/v2/axios';
import { getPresignedUploadUrl, getS3PresignedUrlFromPath, uploadFileToPresignedUrl } from '@/utils/s3';

import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';
import MeetingTiptap from './MeetingTiptap';

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 90 }} variant="body2">
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right' }} variant="body2">
        {children}
      </Typography>
    </Stack>
  );
}

function isPdfFileName(name: string): boolean {
  return /\.pdf$/i.test(name ?? '');
}

function isImageFileName(name: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name ?? '');
}

// ─── LEFT: Agenda ───────────────────────────────────────────────────────────

interface MeetingDescriptionProps {
  meeting?: Meeting | null;
  isLoading: boolean;
}

export function MeetingDescription({ meeting, isLoading }: MeetingDescriptionProps): React.JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} width="30%" variant="text" />
        <Skeleton height={60} variant="rectangular" sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  return (
    <Box>
      <SectionLabel>Agenda</SectionLabel>
      <Box sx={{ mt: 1.5 }}>
        {meeting?.agenda ? (
          <Typography color="text.primary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: '0.9375rem' }} variant="body2">
            {meeting.agenda}
          </Typography>
        ) : (
          <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
            No agenda set for this meeting
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── LEFT: Notes ─────────────────────────────────────────────────────────────

interface MeetingNotesProps {
  meeting: Meeting | null;
}

export function MeetingNotes({ meeting }: MeetingNotesProps): React.JSX.Element {
  return (
    <Box>
      <SectionLabel>Meeting Notes & Content</SectionLabel>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }} variant="caption">
        Use the rich text editor to add detailed meeting notes.
      </Typography>
      <MeetingTiptap meeting={meeting} />
    </Box>
  );
}

// ─── RIGHT: Sidebar ─────────────────────────────────────────────────────────

interface MeetingSidebarProps {
  meeting?: Meeting | null;
  isLoading: boolean;
  onRefetch: () => void;
}

export function MeetingSidebar({ meeting, isLoading, onRefetch }: MeetingSidebarProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [uploadingDoc, setUploadingDoc] = React.useState(false);
  const [previewFile, setPreviewFile] = React.useState<MeetingFile | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const isMeetingPast = meeting?.held_on
    ? dayjs(meeting.held_on).startOf('day').isBefore(dayjs().startOf('day'))
    : false;

  const updateHeldOnMutation = useMutation({
    mutationFn: async (heldOn: string) => {
      const response = await axios.patch(`/api/v1/meetings/${meeting?.id}`, { held_on: heldOn });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Meeting date updated');
      queryClient.invalidateQueries({ queryKey: ['meeting', meeting?.id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Unable to update meeting');
    },
  });

  const handleOpenPreview = React.useCallback(async (file: MeetingFile) => {
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await getS3PresignedUrlFromPath(file.key);
      setPreviewUrl(url);
    } catch {
      toast.error('Unable to load document');
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleClosePreview = React.useCallback(() => {
    setPreviewFile(null);
    setPreviewUrl(null);
  }, []);

  const handleOpenInNewTab = React.useCallback(async (file: MeetingFile) => {
    try {
      const url = await getS3PresignedUrlFromPath(file.key);
      window.open(url, '_blank');
    } catch {
      toast.error('Unable to open document');
    }
  }, []);

  const handleUploadDocument = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f || !meeting?.id) return;
      setUploadingDoc(true);
      try {
        const { url, key } = await getPresignedUploadUrl(f);
        await uploadFileToPresignedUrl(url, f);
        const existing = (meeting.files ?? []) as MeetingFile[];
        const files = [...existing, { key, file_name: f.name }];
        await axios.patch(`/api/v1/meetings/${meeting.id}`, { files });
        toast.success('Document uploaded');
        onRefetch();
      } catch {
        toast.error('Failed to upload document');
      } finally {
        setUploadingDoc(false);
        e.target.value = '';
      }
    },
    [meeting?.id, meeting?.files, onRefetch]
  );

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={18} variant="text" />
        ))}
      </Stack>
    );
  }

  return (
    <>
      <Stack divider={<Divider />} spacing={0}>
        {/* Details */}
        <Box sx={{ pb: 2 }}>
          <SectionLabel>Details</SectionLabel>
          <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
            <Box sx={{ py: 1.25 }}>
              <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 1 }} variant="body2">
                Meeting date
              </Typography>
              <DatePicker
                label="Held on"
                minDate={isMeetingPast ? undefined : dayjs()}
                disabled={isMeetingPast}
                value={meeting?.held_on ? dayjs(meeting.held_on) : null}
                onChange={(v) => {
                  if (v) {
                    updateHeldOnMutation.mutate(dayjs(v).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'));
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    disabled: isMeetingPast || updateHeldOnMutation.isPending,
                    helperText: isMeetingPast ? 'Date cannot be changed after the meeting has passed' : undefined,
                  },
                }}
              />
            </Box>
            {meeting?.created_by && (
              <KVRow label="Created by">
                {meeting.created_by.first_name} {meeting.created_by.last_name}
              </KVRow>
            )}
            {meeting?.created_at && (
              <KVRow label="Created">{dayjs(meeting.created_at).format('MMM D, YYYY')}</KVRow>
            )}
          </Stack>
        </Box>

        {/* Documents */}
        <Box sx={{ py: 2 }}>
          <SectionLabel>Documents</SectionLabel>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {meeting?.files?.length ? (
              meeting.files.map((file: MeetingFile, idx: number) => (
                <Button
                  key={file.key + String(idx)}
                  size="small"
                  startIcon={<FileArrowDown size={16} />}
                  onClick={() => handleOpenPreview(file)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {file.file_name ?? file.key}
                </Button>
              ))
            ) : (
              <Typography color="text.secondary" variant="body2">
                No documents attached
              </Typography>
            )}
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<UploadSimple size={16} />}
              disabled={uploadingDoc}
            >
              {uploadingDoc ? 'Uploading...' : 'Upload document'}
              <input type="file" hidden accept=".pdf,.doc,.docx,.txt,image/*" onChange={handleUploadDocument} />
            </Button>
          </Stack>
        </Box>
      </Stack>

      {/* Preview dialog */}
      <Dialog
        open={Boolean(previewFile)}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', width: '90vw' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" noWrap sx={{ flex: 1, pr: 2 }}>
            {previewFile?.file_name ?? previewFile?.key ?? 'Document'}
          </Typography>
          <IconButton aria-label="Close" onClick={handleClosePreview} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
          {previewLoading ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Loading document…
            </Alert>
          ) : previewUrl && previewFile ? (
            (() => {
              const name = previewFile.file_name ?? previewFile.key ?? '';
              if (isPdfFileName(name)) {
                return (
                  <iframe
                    src={previewUrl}
                    title={name}
                    style={{ flex: 1, width: '100%', minHeight: 480, border: 'none' }}
                  />
                );
              }
              if (isImageFileName(name)) {
                return (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={name}
                    sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', m: 'auto', display: 'block' }}
                  />
                );
              }
              return (
                <Alert
                  severity="info"
                  sx={{ m: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<FileArrowDown size={18} />}
                      onClick={() => previewFile && handleOpenInNewTab(previewFile)}
                    >
                      Open in new tab
                    </Button>
                  }
                >
                  This file type cannot be previewed here. Open it in a new tab to view.
                </Alert>
              );
            })()
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
